import { GridDimensions } from './geometry';
import { median } from '~/utils/number';

/**
 * Separates the couch's structural plate from its foam core.
 *
 * The plate is carbon fibre, thin enough that partial-volume averaging
 * pulls it well below its bulk value — measured around −330 to −560 HU on
 * a 1.2 mm reconstruction. The foam sits near −950, air near −1000. A
 * threshold between them reads only the plate, which is what the couch
 * TOP is: thresholding low enough to include the foam finds the top of the
 * foam instead, several millimetres inside the couch.
 */
const PLATE_HU = -700;

/**
 * Fraction of a row that must be plate for the row to be part of the flat
 * insert.
 *
 * Deliberately not near 1: the couch is not always wider than the
 * reconstructed field. A large-FOV simulation scan (615 mm across, against
 * a couch nearer 530 mm) leaves the insert covering under 90% of every
 * row, and a threshold set for the narrow-FOV case finds nothing in it.
 * What this still excludes is the scanner's own curved couch below, whose
 * shell is a thin arc covering a few percent of any row it crosses.
 */
const PLATE_COVERAGE = 0.5;

/**
 * Soft tissue, and the fraction of the row's MIDDLE that must reach it for
 * the row to be patient rather than couch.
 *
 * Tissue passes {@link PLATE_HU} as easily as carbon does and spans as much
 * of a row, so coverage alone cannot tell a patient from an insert.
 * Reaching soft-tissue values can — except that a couch is not always far
 * from them. Index bars, indexing rails and the clamps that hold an
 * immobilisation frame are metal, and a couch stopping the scan as though
 * it were the patient leaves the couch unfound.
 *
 * What separates them is WHERE, not how much. A patient lies on the middle
 * of the couch; its hardware sits at the ends. Reading the middle half of
 * the row alone sees the one and not the other, and the two separate
 * completely: across a hundred torso series the couches carrying bars held
 * no central tissue at all, and the leanest genuine patient row held 42% of
 * its middle.
 */
const TISSUE_HU = -100;
const TISSUE_CENTRE_COVERAGE = 0.2;

/**
 * How continuous a row's material must be, measured across its own extent
 * rather than across the image: material pixels divided by the span from
 * the first to the last of them.
 *
 * This is what separates the couch from what is laid on it. A plate is a
 * manufactured sheet and reads as one unbroken run. A blanket or pad
 * between patient and couch is the same material at the same density, wide
 * enough and dense enough to pass every other test here — but it drapes in
 * folds, and the profile cuts air gaps through them. Gaps are the signal:
 * the couch is the topmost thing under the patient that has none.
 *
 * Not 1.0, so a bolt hole or an index notch does not disqualify a plate.
 */
const PLATE_CONTINUITY = 0.95;

/** Slices sampled across Z. The insert is flat, so every slice sees the
 *  same couch top and this only needs enough of them to outvote the ones
 *  where something else intervenes. */
const SAMPLED_SLICES = 16;

/**
 * The couch top, as a continuous voxel Y in `grid`, found by reading the
 * flat insert's top plate — or null when no sampled slice shows one.
 *
 * Reads the couch itself rather than the patient resting on it, so unlike
 * the BODY-based estimate it is unaffected by how far the pad compresses.
 * It needs the couch to be IN the image: a scan reconstructed tightly
 * around the patient, or one whose field stops above the insert, yields
 * nothing and says so.
 *
 * Assumes the flat insert a CT simulator always carries. The scanner's own
 * curved couch may sit below it and is ignored — its shell never covers
 * enough of a row — but a scan carrying ONLY a curved tabletop has no
 * single top row, and what this would return for one is not its top.
 *
 * Assumes, too, that the topmost rigid sheet under the patient IS the
 * couch. A rigid immobilisation accessory laid on the couch — a breast
 * board, a base plate — is plate-dense, plate-wide and unbroken, so it
 * satisfies every test here and is returned in the couch's place, several
 * centimetres high. This is the reading's known failure case rather than a
 * detectable one: nothing in a cross-section distinguishes an accessory's
 * top plate from a couch's. It is why the result is presented as an
 * estimate with its source named and left draggable — on such a series the
 * user moves the line down to the couch itself.
 *
 * `voxels` are Hounsfield units in Image3D order
 * (z*sizeX*sizeY + y*sizeX + x); the caller must not re-apply
 * RescaleSlope/Intercept, which dicomImageSlice already did.
 */
export function couchTopFromImage(
    voxels: Int16Array,
    grid: GridDimensions
): number | null {
    const expected = grid.sizeX * grid.sizeY * grid.sizeZ;
    if (voxels.length !== expected) {
        throw new Error(
            `couchTopFromImage: voxel buffer length ${voxels.length} does not match grid ${grid.sizeX}x${grid.sizeY}x${grid.sizeZ} (expected ${expected})`
        );
    }

    const tops: number[] = [];
    for (const z of sampleSlices(grid.sizeZ)) {
        const top = couchTopOnSlice(voxels, grid, z);
        if (top !== null) {
            tops.push(top);
        }
    }
    if (tops.length === 0) {
        return null;
    }
    // The median, so a slice where something crosses the couch — an arm, a
    // bolus, the edge of a headrest — cannot move the answer.
    return median(tops);
}

/** Evenly spaced slice indices, ends included, never more than there are. */
function sampleSlices(sizeZ: number): number[] {
    const count = Math.min(SAMPLED_SLICES, sizeZ);
    if (count === 1) {
        return [Math.floor(sizeZ / 2)];
    }
    return Array.from({ length: count }, (_, i) =>
        Math.round((i * (sizeZ - 1)) / (count - 1))
    );
}

/**
 * The top of the insert on one slice, as a voxel Y, or null when the slice
 * shows no insert.
 *
 * Scans upward from the bottom and keeps the HIGHEST row that is plate-
 * dense, plate-wide and unbroken, stopping at the patient.
 *
 * All three matter. A couch cross-section is two plates with foam between,
 * so the first run from below is the underside and the one above it is the
 * top — taking the first would sit a couch thickness too low. The patient
 * stops the scan because tissue is plate-dense and patient-wide, and
 * anything above them is not couch — read over the row's middle, where a
 * patient is and a couch's metalwork is not. And what lies BETWEEN patient
 * and couch — a blanket, a pad — passes all of those tests and is rejected
 * only by continuity: it drapes in folds, and the profile cuts air through
 * them.
 *
 * Returned as the row's TOP edge, which is the row index itself in the
 * corner convention placement works in: the couch top is the surface where
 * the first plate row begins, not the middle of it.
 */
function couchTopOnSlice(
    voxels: Int16Array,
    grid: GridDimensions,
    z: number
): number | null {
    const { sizeX, sizeY } = grid;
    const plane = z * sizeX * sizeY;
    const plateNeeded = sizeX * PLATE_COVERAGE;
    // The middle half of the row, and how much of THAT must be tissue.
    const centreFrom = Math.floor(sizeX / 4);
    const centreTo = sizeX - centreFrom;
    const tissueNeeded = (centreTo - centreFrom) * TISSUE_CENTRE_COVERAGE;

    let top: number | null = null;
    for (let y = sizeY - 1; y >= 0; y--) {
        let plate = 0;
        let tissue = 0;
        let first = -1;
        let last = -1;
        const row = plane + y * sizeX;
        for (let x = 0; x < sizeX; x++) {
            const hu = voxels[row + x];
            if (hu >= PLATE_HU) {
                plate++;
                last = x;
                if (first < 0) {
                    first = x;
                }
                if (hu >= TISSUE_HU && x >= centreFrom && x < centreTo) {
                    tissue++;
                }
            }
        }
        if (plate < plateNeeded) {
            continue;
        }
        if (tissue >= tissueNeeded) {
            break;
        }
        if (plate / (last - first + 1) < PLATE_CONTINUITY) {
            continue;
        }
        top = y;
    }
    return top;
}
